import { Trans } from '@lingui/react/macro';

import AlertBar from '@/design_system/AlertBar';
import Stack from '@/design_system/Stack';
import IconTruck from '@/icons/Truck.svg';
import { RequestWithRelations } from '@/models/request';
import { hasPermission } from '@/models/user';
import { useCurrentSession } from '@/services/auth';

import { PendingClientActionReminder } from '../../PendingClientActionReminder/PendingClientActionReminder';

export const TransitStepMessage = ({ request }: { request: RequestWithRelations }) => {
  const { currentSession } = useCurrentSession();

  const areAllArticlesInPendingClientPickup = request.articles.every(
    (article) =>
      article.step?.step === 'transit' &&
      !!request.storeId &&
      article.atEntityId === request.storeId &&
      article.toClient &&
      article.step?.config.clientPickup
  );

  const isClientCommunicationEnabled =
    request.organization.config.clientCommunication?.notifications?.enabled;

  const canSendPickupReminder =
    isClientCommunicationEnabled &&
    hasPermission(currentSession, 'write_client_communication', [
      {
        organizationId: request.organizationId,
        entityId: null,
      },
      {
        entityId: request.afterSalesId,
      },
      {
        entityId: request.storeId,
      },
    ]);

  let messageContent;

  if (areAllArticlesInPendingClientPickup) {
    if (isClientCommunicationEnabled) {
      messageContent = (
        <Trans id="requests.pending-client-delivery-message.text">
          The package is at the store and the client has been notified that they can pick up the
          items.
        </Trans>
      );
    } else {
      messageContent = (
        <Trans id="requests.pending-client-delivery-message.no-communication.text">
          The package is at the store and the client must now come pick it up.
        </Trans>
      );
    }
  } else {
    if (isClientCommunicationEnabled) {
      messageContent = (
        <Trans id="requests.pending-client-delivery-message.text.some">
          Some items are at the store and the client has been notified that they can pick them up.
        </Trans>
      );
    } else {
      messageContent = (
        <Trans id="requests.pending-client-delivery-message.no-communication.text.some">
          Some items are at the store and the client must now come pick them up.
        </Trans>
      );
    }
  }

  return (
    <AlertBar
      type="info"
      size="large"
      icon={<IconTruck />}
      title={
        <Trans id="requests.pending-client-delivery-message.title">Waiting for client pickup</Trans>
      }
    >
      <Stack gap="0.5rem">
        <p>
          {messageContent}
          {canSendPickupReminder && (
            <>
              {' '}
              <Trans id="requests.pending-client-delivery-message.text.reminder">
                If they don't come, feel free to send them an email reminder.
              </Trans>
            </>
          )}
        </p>
        {canSendPickupReminder && <PendingClientActionReminder id={request.id} type="pickup" />}
      </Stack>
    </AlertBar>
  );
};
